import axios from 'axios';
import { registryData } from '../stores/registryStore';

const token = import.meta.env.PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN;
// const token = import.meta.env.PUBLIC_SHOPIFY_STAGING_STOREFRONT_ACCESS_TOKEN;

// Configuration
const CONFIG = {
	chunkSize: 3, // Reduced from 5 to 3
	requestDelay: 1000, // Increased delay between requests
	maxConcurrent: 500, // Maximum concurrent requests
	retries: 2, // Number of retries
	retryDelay: 2000, // Delay between retries
};

// Utility for delays
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Semaphore for limiting concurrent requests
class Semaphore {
	constructor(max) {
		this.max = max;
		this.tokens = max;
		this.waiting = [];
	}

	async acquire() {
		if (this.tokens > 0) {
			this.tokens--;
			return;
		}
		await new Promise((resolve) => this.waiting.push(resolve));
	}

	release() {
		if (this.waiting.length > 0) {
			const next = this.waiting.shift();
			next();
		} else {
			this.tokens++;
		}
	}
}

// Create semaphore instance
const semaphore = new Semaphore(CONFIG.maxConcurrent);

// Enhanced fetch with resource management
async function safeFetch(url, options, retryCount = 0) {
	try {
		await semaphore.acquire(); // Wait for available slot
		const response = await fetch(url, options);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();
		await delay(CONFIG.requestDelay); // Delay after successful request
		return data;
	} catch (error) {
		if (retryCount < CONFIG.retries) {
			await delay(CONFIG.retryDelay);
			return safeFetch(url, options, retryCount + 1);
		}
		throw error;
	} finally {
		semaphore.release(); // Always release the semaphore
	}
}

// Modified Shopify request
export async function sendShopifyStorefrontRequest({ query, variables }) {
	try {
		const { data } = await safeFetch(
			'https://monicaandandyonline.myshopify.com/api/2023-07/graphql.json',
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Shopify-Storefront-Access-Token': token,
				},
				body: JSON.stringify({ query, variables }),
			}
		);
		return data;
	} catch (error) {
		console.error('Shopify request failed:', error);
		return { inventory: 0 }; // Safe fallback
	}
}

// export async function sendShopifyStorefrontRequest({ query, variables }) {
//     const result = await fetch(
//         // 'https://monicaandandyonline.myshopify.com/api/2025-01/graphql.json',
//         "https://monicaandandy-broadwayplaza.myshopify.com/api/2023-07/graphql.json",
//         {
//             method: 'POST',
//             headers: {
//                 'Content-Type': 'application/json',
//                 'X-Shopify-Storefront-Access-Token': token,
//             },
//             body: JSON.stringify({ query, variables }),
//         }
//     )

//     if (!result.ok) {
//         console.error('error with Shopify request', result)
//         return false
//     }

//     const { data } = await result.json()
//     return data
// }

// // Utility function for delay between retries
// const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// // Retry configuration
// const RETRY_CONFIG = {
//     maxRetries: 3,
//     initialDelay: 1000,
//     maxDelay: 5000
// };

// // Wrapper function to handle retries with exponential backoff
// async function fetchWithRetry(url, options, retryCount = 0) {
//     try {
//         const response = await fetch(url, options);
//         if (!response.ok) {
//             throw new Error(`HTTP error! status: ${response.status}`);
//         }
//         return response;
//     } catch (error) {
//         if (retryCount >= RETRY_CONFIG.maxRetries) {
//             throw new Error(`Failed after ${RETRY_CONFIG.maxRetries} retries: ${error.message}`);
//         }

//         const waitTime = Math.min(
//             RETRY_CONFIG.initialDelay * Math.pow(2, retryCount),
//             RETRY_CONFIG.maxDelay
//         );
//         await delay(waitTime);
//         return fetchWithRetry(url, options, retryCount + 1);
//     }
// }

// // Modified sendShopifyStorefrontRequest with retry logic
// export async function sendShopifyStorefrontRequest({ query, variables }) {
//     try {
//         const response = await fetchWithRetry(
//             "https://monicaandandy-broadwayplaza.myshopify.com/api/2023-07/graphql.json",
//             {
//                 method: 'POST',
//                 headers: {
//                     'Content-Type': 'application/json',
//                     'X-Shopify-Storefront-Access-Token': token,
//                 },
//                 body: JSON.stringify({ query, variables }),
//             }
//         );

//         const { data } = await response.json();
//         return data;
//     } catch (error) {
//         console.error('Failed to fetch from Shopify:', error);
//         throw error;
//     }
// }

export async function createCart() {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation CreateCart {
          cartCreate {
            cart {
              checkoutUrl
              id
            }
          }
        }
      `,
		variables: {},
	});

	if (!data) {
		return {
			statusCode: 500,
			body: JSON.stringify({
				message: 'There was a problem creating a cart.',
			}),
		};
	}

	return {
		statusCode: 200,
		data: {
			cartId: data.cartCreate?.cart?.id,
			checkoutUrl: data.cartCreate?.cart?.checkoutUrl,
		},
	};
}

export async function addToCart(cartId, lines) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
                userErrors {
                    field
                    message
                  }

                cart {
                    id
                    cost {
                        subtotalAmount {
                            amount
                        }
                        totalTaxAmount {
                            amount
                        }
                        totalAmount {
                            amount
                        }
                    }
                    discountCodes {
                        applicable
                        code
                      }
                    discountAllocations {
                        ... on CartAutomaticDiscountAllocation {
                          discountedAmount {
                            amount
                            currencyCode
                          }
                          title
                        }
                        ... on CartCodeDiscountAllocation {
                          code
                          discountedAmount {
                            amount
                            currencyCode
                          }
                        }
                        ... on CartCustomDiscountAllocation {
                          discountedAmount {
                            amount
                            currencyCode
                          }
                          title
                        }
                        ... on CartDiscountAllocation {
                          discountedAmount {
                            amount
                            currencyCode
                          }
                        }
                        discountedAmount {
                          amount
                          currencyCode
                        }
                      }
                    lines(first: 100) {
                      edges {
                        node {
                            discountAllocations {
                                ... on CartAutomaticDiscountAllocation {
                                  discountedAmount {
                                    amount
                                    currencyCode
                                  }
                                  title
                                }
                                ... on CartCodeDiscountAllocation {
                                  code
                                  discountedAmount {
                                    amount
                                    currencyCode
                                  }
                                }
                                ... on CartCustomDiscountAllocation {
                                  discountedAmount {
                                    amount
                                    currencyCode
                                  }
                                  title
                                }
                                ... on CartDiscountAllocation {
                                  discountedAmount {
                                    amount
                                    currencyCode
                                  }
                                }
                                discountedAmount {
                                  amount
                                  currencyCode
                                }
                              }
                          id
                          quantity
                          cost {
                            amountPerQuantity {
                              amount
                              currencyCode
                            }
                            compareAtAmountPerQuantity {
                              amount
                              currencyCode
                            }
                            subtotalAmount {
                              amount
                              currencyCode
                            }
                            totalAmount {
                              amount
                              currencyCode
                            }
                          }
                          merchandise {
                            ... on ProductVariant {
                                sku
                                id
                                title
                                selectedOptions {
                                    name
                                    value
                                }
                              product {
                                id
                                title
                                handle
                                options {
                                    id
                                    name
                                    values
                                }
                                vendor
                            productType
                                tags
                                featuredImage {
                                    url
                                    altText
                                }

                              }
                            }
                          }
                            attributes {
                                key
                                value
                            }
                        }
                      }
                    }
                  }
                }
        }
      `,
		variables: {
			cartId: cartId,
			lines,
		},
	});

	if (!data) {
		return {
			statusCode: 500,
			body: JSON.stringify({
				message: 'There was a problem adding to cart.',
			}),
		};
	}

	return {
		statusCode: 200,
		body: data,
	};
}

export async function updateCart(cartId, lines) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation UpdateCart($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart {
                id
                createdAt
                updatedAt
                cost {
                    subtotalAmount {
                        amount
                    }
                    totalTaxAmount {
                        amount
                    }
                    totalAmount {
                        amount
                    }

                }
                discountCodes {
                    applicable
                    code
                  }
                discountAllocations {
                    ... on CartAutomaticDiscountAllocation {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                      title
                    }
                    ... on CartCodeDiscountAllocation {
                      code
                      discountedAmount {
                        amount
                        currencyCode
                      }
                    }
                    ... on CartCustomDiscountAllocation {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                      title
                    }
                    ... on CartDiscountAllocation {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                    }
                    discountedAmount {
                      amount
                      currencyCode
                    }
                  }
                lines(first: 100) {
                      edges {
                        node {
                            discountAllocations {
                                ... on CartAutomaticDiscountAllocation {
                                  discountedAmount {
                                    amount
                                    currencyCode
                                  }
                                  title
                                }
                                ... on CartCodeDiscountAllocation {
                                  code
                                  discountedAmount {
                                    amount
                                    currencyCode
                                  }
                                }
                                ... on CartCustomDiscountAllocation {
                                  discountedAmount {
                                    amount
                                    currencyCode
                                  }
                                  title
                                }
                                ... on CartDiscountAllocation {
                                  discountedAmount {
                                    amount
                                    currencyCode
                                  }
                                }
                                discountedAmount {
                                  amount
                                  currencyCode
                                }
                              }
                          id
                          quantity
                          cost {
                            amountPerQuantity {
                              amount
                              currencyCode
                            }
                            compareAtAmountPerQuantity {
                              amount
                              currencyCode
                            }
                            subtotalAmount {
                              amount
                              currencyCode
                            }
                            totalAmount {
                              amount
                              currencyCode
                            }
                          }
                          merchandise {
                            ... on ProductVariant {
                                sku
                                id
                                title
                                selectedOptions {
                                    name
                                    value
                                }
                              product {
                                id
                                title
                                handle
                                options {
                                    id
                                    name
                                    values
                                }
                                vendor
                            productType
                                tags
                                featuredImage {
                                    url
                                    altText
                                }
                                priceRange {
                                    maxVariantPrice {
                                        amount
                                    }
                                }
                              }
                            }
                          }
                          attributes {
                            key
                            value
                          }
                        }
                      }
                    }
                    attributes {
                        key
                        value
                      }

          }
        }
}
      `,
		variables: {
			cartId: cartId,
			lines,
		},
	});

	if (!data) {
		return {
			statusCode: 500,
			body: JSON.stringify({
				message: 'There was a problem adding to cart.',
			}),
		};
	}

	return {
		statusCode: 200,
		body: data,
	};
}

export async function getCart(cartId) {
	const data = await sendShopifyStorefrontRequest({
		query: `
          query GetCart($cartId: ID!) {
            cart(id: $cartId) {
                id
                checkoutUrl
                note
                cost {
                    subtotalAmount {
                        amount
                    }
                  totalAmount {
                    amount
                  }
                }
                discountCodes {
                    applicable
                    code
                  }
                discountAllocations {
                    ... on CartAutomaticDiscountAllocation {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                      title
                    }
                    ... on CartCodeDiscountAllocation {
                      code
                      discountedAmount {
                        amount
                        currencyCode
                      }
                    }
                    ... on CartCustomDiscountAllocation {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                      title
                    }
                    ... on CartDiscountAllocation {
                      discountedAmount {
                        amount
                        currencyCode
                      }
                    }
                    discountedAmount {
                      amount
                      currencyCode
                    }
                  }
                lines(first: 100) {
                  edges {
                    node {
                        discountAllocations {
                            ... on CartAutomaticDiscountAllocation {
                              discountedAmount {
                                amount
                                currencyCode
                              }
                              title
                            }
                            ... on CartCodeDiscountAllocation {
                              code
                              discountedAmount {
                                amount
                                currencyCode
                              }
                            }
                            ... on CartCustomDiscountAllocation {
                              discountedAmount {
                                amount
                                currencyCode
                              }
                              title
                            }
                            ... on CartDiscountAllocation {
                              discountedAmount {
                                amount
                                currencyCode
                              }
                            }
                            discountedAmount {
                              amount
                              currencyCode
                            }
                          }
                      quantity
                      id
                      cost {
                        amountPerQuantity {
                          amount
                          currencyCode
                        }
                        compareAtAmountPerQuantity {
                          amount
                          currencyCode
                        }
                        subtotalAmount {
                          amount
                          currencyCode
                        }
                        totalAmount {
                          amount
                          currencyCode
                        }
                      }
                      merchandise {
                        ... on ProductVariant {
                          sku
                          id
                          title
                          selectedOptions {
                            name
                            value
                          }
                          product {
                            id
                            title
                            handle
                            options {
                              id
                              name
                              values
                            }
                            vendor
                            productType
                            featuredImage {
                              url
                              altText
                            }
                            priceRange {
                              maxVariantPrice {
                                amount
                              }
                            }
                            tags
                          }
                        }
                      }
                      attributes {
                        key
                        value
                      }
                    }
                  }
                }
                attributes {
                  key
                  value
                }
            }
          }
        `,
		variables: { cartId },
	});

	return {
		statusCode: 200,
		body: data,
	};
}

export async function updateCartDiscountCodes(cartId, discountCodes) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
            cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                cart {
                    discountCodes {
                        applicable
                        code
                    }
                    discountAllocations {
                        ... on CartAutomaticDiscountAllocation {
                          discountedAmount {
                            amount
                            currencyCode
                          }
                          title
                        }
                        ... on CartCodeDiscountAllocation {
                          code
                          discountedAmount {
                            amount
                            currencyCode
                          }
                        }
                        ... on CartCustomDiscountAllocation {
                          discountedAmount {
                            amount
                            currencyCode
                          }
                          title
                        }
                        ... on CartDiscountAllocation {
                          discountedAmount {
                            amount
                            currencyCode
                          }
                        }
                        discountedAmount {
                          amount
                          currencyCode
                        }
                      }
                    id
                    updatedAt
                    cost {
                        subtotalAmount {
                            amount
                        }
                        totalTaxAmount {
                            amount
                        }
                        totalAmount {
                            amount
                        }

                    }
                    lines(first: 100) {
                        edges {
                          node {
                              discountAllocations {
                                  ... on CartAutomaticDiscountAllocation {
                                    discountedAmount {
                                      amount
                                      currencyCode
                                    }
                                    title
                                  }
                                  ... on CartCodeDiscountAllocation {
                                    code
                                    discountedAmount {
                                      amount
                                      currencyCode
                                    }
                                  }
                                  ... on CartCustomDiscountAllocation {
                                    discountedAmount {
                                      amount
                                      currencyCode
                                    }
                                    title
                                  }
                                  ... on CartDiscountAllocation {
                                    discountedAmount {
                                      amount
                                      currencyCode
                                    }
                                  }
                                  discountedAmount {
                                    amount
                                    currencyCode
                                  }
                                }
                            quantity
                            id
                            cost {
                              amountPerQuantity {
                                amount
                                currencyCode
                              }
                              compareAtAmountPerQuantity {
                                amount
                                currencyCode
                              }
                              subtotalAmount {
                                amount
                                currencyCode
                              }
                              totalAmount {
                                amount
                                currencyCode
                              }
                            }
                            merchandise {
                              ... on ProductVariant {
                                sku
                                id
                                title
                                selectedOptions {
                                  name
                                  value
                                }
                                product {
                                  id
                                  title
                                  handle
                                  options {
                                    id
                                    name
                                    values
                                  }
                                  vendor
                                   productType
                                  featuredImage {
                                    url
                                    altText
                                  }
                                  priceRange {
                                    maxVariantPrice {
                                      amount
                                    }
                                  }
                                  tags
                                }
                              }
                            }
                            attributes {
                              key
                              value
                            }
                          }
                        }
                      }
                  }

                  userErrors {
                    field
                    message
                  }

            }
          }
        `,
		variables: { cartId, discountCodes },
	});

	return {
		statusCode: 200,
		body: data,
	};
}

export async function updateCartNote(cartId, note) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation cartNoteUpdate($cartId: ID!, $note: String!) {
            cartNoteUpdate(cartId: $cartId, note: $note) {
                cart {
                        id
                        updatedAt
                        note
                    }
                    userErrors {
                        field
                        message
                    }

                }
            }
        `,
		variables: { cartId, note },
	});

	return {
		statusCode: 200,
		body: data,
	};
}

export async function updateCartAttributes(cartId, attributes) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation cartAttributesUpdate($attributes: [AttributeInput!]!, $cartId: ID!) {
            cartAttributesUpdate(attributes: $attributes, cartId: $cartId) {
                cart {
                    id
                    note
                    attributes {
                      key
                      value
                    }
                  }
                    userErrors {
                        field
                        message
                    }

                }

            }
        `,
		variables: { cartId, attributes },
	});

	return {
		statusCode: 200,
		body: data,
	};
}

export async function updateCheckoutAttributes(checkoutId, input) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation checkoutAttributesUpdateV2($checkoutId: ID!, $input: CheckoutAttributesUpdateV2Input!) {
            checkoutAttributesUpdateV2(checkoutId: $checkoutId, input: $input) {
              checkout {
                id
                attributes {
                    key
                    value
                }
                note
              }
              checkoutUserErrors {
                code
                field
                errors
              }
            }
          }
        `,
		variables: { checkoutId, input },
	});

	return {
		statusCode: 200,
		body: data,
	};
}

export async function getCollectionByHandle(handle) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query collectionByHandle($handle: String!) {
            collectionByHandle (handle: $handle) {
                    id
                    title
                    handle
                    descriptionHtml
                    description
                    seo {
                        title
                        description
                    }
                }
            }
        `,
		variables: { handle },
	});

	return {
		statusCode: 200,
		body: data,
	};
}

export async function getPageByHandle(handle) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query pageByHandle($handle: String!) {
            pageByHandle (handle: $handle) {
                    id
                    title
                    handle
                    body
                    bodySummary
                    onlineStoreUrl
                    seo {
                        title
                        description
                    }
                    metafield (namespace: "custom_fields", key: "default_image") {
                        id
                        key
                        value

                      }
                }
            }
        `,
		variables: { handle },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function getPageById(id, identifiers) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query pageById($id: ID, $identifiers: [HasMetafieldsIdentifier!]! ) {
            page(id: $id) {
              id
              title
              handle
              body
              bodySummary
              onlineStoreUrl
              metafields(identifiers: $identifiers){
                id
                key
                value
                namespace
                type

              }
            }

          }
        `,
		variables: { id, identifiers },
	});

	//

	return {
		statusCode: 200,
		data: data,
	};
}

export async function getBlogPosts(first, cursor) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query getBlogArticles($first: Int, $cursor: String) {
            articles (first: $first, after: $cursor) {
                edges {
                    node {
                        id
                        title
                        handle
                        contentHtml
                        excerptHtml
                        image {
                            url
                            altText
                        }
                        onlineStoreUrl
                        tags
                    }
                }
                pageInfo {
                    hasNextPage
                    hasPreviousPage
                    endCursor
                    startCursor
                }
            }
        }
        `,
		variables: { first, cursor },
	});

	return {
		statusCode: 200,
		body: data,
	};
}

export async function getPostByHandle(handle) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query articles($handle: String) {
            articles (first: 10, query: $handle) {
                    edges {
                        node {
                            contentHtml
                            excerptHtml
                            handle
                            id
                            title
                            tags
                            seo {
                                title
                                description
                            }
                            image {
                                url
                                altText
                            }
                        }
                    }

                    }

                }
        `,
		variables: { handle },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function getBlogPostByHandle(handle) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query blog($handle: String!) {
            blog(handle: "ma-edit") {
              id
              articleByHandle(handle: $handle) {
                id
                title
                onlineStoreUrl
                  content
                contentHtml
                excerpt
                excerptHtml
                tags
                image {
                  altText
                  id
                  url
                }
                seo {
                  title
                  description
                }
                publishedAt
                }
            }
          }
        `,
		variables: { handle },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function getBlogArticleTags() {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query {
            blog(handle: "ma-edit") {
              id
              articles(first: 250) {
                edges {
                  node {
                    id
                    tags
                  }
                }
              }
            }
          }
        `,
		variables: {},
	});

	//

	const tags =
		data.blog.articles.edges.map((edge) => edge.node.tags).flat() || [];

	//

	const uniqueTags = [...new Set(tags)];

	//

	return {
		statusCode: 200,
		data: uniqueTags,
	};
}

export async function createCustomer(firstName, lastName, email, password) {
	//
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerCreate($input: CustomerCreateInput!) {
            customerCreate(input: $input) {
              customerUserErrors {
                code
                field
                message
              }
              customer {
                id
                firstName
                lastName
                acceptsMarketing
                email
                phone
              }

            }
          }
        `,
		variables: {
			input: {
				firstName,
				lastName,
				email,
				password,
			},
		},
	});

	//

	return {
		statusCode: 200,
		data: data,
	};
}

export async function createCustomerAccessToken(email, password) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
            customerAccessTokenCreate(input: $input) {
            customerUserErrors {
                code
                field
                message
            }
            customerAccessToken {
                accessToken
                expiresAt
            }
            }
        }
        `,
		variables: {
			input: {
				email: email,
				password: password,
			},
		},
	});

	// await registryLogIn(email, password)

	return {
		statusCode: 200,
		data: data,
	};
}

export async function customerActivate(id, activationToken, password) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerActivate($id: ID!, $input: CustomerActivateInput!) {
        customerActivate(id: $id, input: $input) {
          customer {
            id
            email
          }
         customerUserErrors {
                code
                field
                message
            }
            customerAccessToken {
                accessToken
                expiresAt
            }
          userErrors {
            field
            message
          }
        }
      }
        `,
		variables: {
			id,
			input: {
				activationToken,
				password,
			},
		},
	});
}

export async function customerActivateByUrl(activationUrl, password) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerActivateByUrl($activationUrl: URL!, $password: String!) {
        customerActivateByUrl(activationUrl: $activationUrl, password: $password) {
          customer {
            id
            email
          }
         customerUserErrors {
                code
                field
                message
            }
            customerAccessToken {
                accessToken
                expiresAt
            }

        }
      }
        `,
		variables: {
			activationUrl,
			password,
		},
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function getCustomer(customerAccessToken) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query getCustomer($customerAccessToken: String!) {
            customer(customerAccessToken: $customerAccessToken) {
                id
                firstName
                lastName
                email
                phone
    }
    }`,
		variables: { customerAccessToken },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function getCustomerId(customerAccessToken) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query getCustomer($customerAccessToken: String!) {
            customer(customerAccessToken: $customerAccessToken) {
                id
    }
    }`,
		variables: { customerAccessToken },
	});

	if (data && data.customer) {
		// Extract the numeric ID from the Shopify GID format
		const numericId = data.customer.id.replace('gid://shopify/Customer/', '');
		return {
			statusCode: 200,
			data: {
				customerId: numericId,
				originalId: data.customer.id
			}
		};
	}

	return {
		statusCode: 404,
		data: null
	};
}

export async function getOrders(first, customerAccessToken, after) {
	//
	const data = await sendShopifyStorefrontRequest({
		query: `
        query getCustomer($customerAccessToken: String!) {
            customer(customerAccessToken: $customerAccessToken) {
                orders (first: ${first}, after: "${after}") {
                    edges {
                        node{
                            customerUrl

                            canceledAt
                            cancelReason
                            currencyCode
                            currentSubtotalPrice {
                                amount
                                currencyCode
                            }
                            currentTotalDuties {
                                amount
                                currencyCode
                            }
                            currentTotalPrice {
                                amount
                                currencyCode
                            }
                            currentTotalTax {
                                amount
                                currencyCode
                            }
                            customerLocale
                            email
                            fulfillmentStatus
                            financialStatus
                            id
                            processedAt

                            discountApplications (first:50) {

                                edges {
                                    node {
                                        __typename
                                        allocationMethod
                                        targetSelection
                                        targetType

                                        value {
                                            __typename
                                        }
                                    }
                                }
                            }
                            lineItems (first: 50) {
                                edges {
                                    node {

                                        title
                                        quantity
                                        currentQuantity
                                        discountedTotalPrice {
                                            amount
                                            currencyCode
                                        }
                                        customAttributes {
                                            key
                                            value
                                        }

                                        discountAllocations {

                                            allocatedAmount {
                                                amount
                                                currencyCode
                                            }
                                            discountApplication {
                                                __typename
                                                allocationMethod
                                                targetSelection
                                                targetType
                                                value {
                                                    __typename
                                                }
                                            }
                                        }
                                        discountedTotalPrice {
                                            amount
                                            currencyCode
                                        }
                                        originalTotalPrice {
                                            amount
                                            currencyCode
                                        }

                                        variant {
                                            id
                                            priceV2 {
                                                amount
                                                currencyCode
                                            }
                                            sku
                                            selectedOptions {
                                                name
                                                value
                                            }
                                            image {
                                                altText
                                                url
                                                id
                                            }
                                            product {
                                                handle
                                                id
                                                vendor
                                            }

                                        }
                                    }
                                }
                            }
                            name
                            orderNumber
                            statusUrl
                            shippingAddress {
                                name
                                address1
                                address2
                                city
                                company
                                province
                                zip
                                country
                                phone
                            }

                            totalShippingPrice {
                                amount
                                currencyCode
                            }
                            totalRefunded {
                                amount
                                currencyCode
                            }
                            successfulFulfillments {
                                trackingCompany
                                trackingInfo {
                                    number
                                    url
                                }

                            }
                            totalTaxV2 {
                                amount
                                currencyCode
                            }

                        }
                    }
                    pageInfo {
                        hasNextPage
                        hasPreviousPage
                        endCursor
                        startCursor
                    }
                }
            }
        }
        `,
		variables: { customerAccessToken },
	});

	return data?.customer;
}

export async function getCustomerDetails(customerAccessToken) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query getCustomer($customerAccessToken: String!) {
            customer(customerAccessToken: $customerAccessToken) {
                id
                firstName
                lastName
                email
                phone
                defaultAddress {
                    id
                    firstName
                    lastName
                    address1
                    address2
                    city
                    company
                    country
                    countryCodeV2
                    province
                    provinceCode
                    zip
                    formatted
                    formattedArea
                    phone
                }
                addresses (first: 50) {
                    edges {
                        node {
                            id
                            firstName
                            lastName
                            address1
                            address2
                            city
                            province
                            provinceCode
                            zip
                            company
                            country
                            countryCodeV2

                            formatted
                            formattedArea
                            phone

                        }
                    }
                }
                orders (first: 50) {
                    edges {
                        node{
                            customerUrl

                            canceledAt
                            cancelReason
                            currencyCode
                            currentSubtotalPrice {
                                amount
                                currencyCode
                            }
                            currentTotalDuties {
                                amount
                                currencyCode
                            }
                            currentTotalPrice {
                                amount
                                currencyCode
                            }
                            currentTotalTax {
                                amount
                                currencyCode
                            }
                            customerLocale
                            email
                            fulfillmentStatus
                            financialStatus
                            id
                            processedAt

                            discountApplications (first:50) {

                                edges {
                                    node {
                                        __typename
                                        allocationMethod
                                        targetSelection
                                        targetType

                                        value {
                                            __typename
                                        }
                                    }
                                }
                            }
                            lineItems (first: 50) {
                                edges {
                                    node {

                                        title
                                        quantity
                                        currentQuantity
                                        discountedTotalPrice {
                                            amount
                                            currencyCode
                                        }
                                        customAttributes {
                                            key
                                            value
                                        }

                                        discountAllocations {

                                            allocatedAmount {
                                                amount
                                                currencyCode
                                            }
                                            discountApplication {
                                                __typename
                                                allocationMethod
                                                targetSelection
                                                targetType
                                                value {
                                                    __typename
                                                }
                                            }
                                        }
                                        discountedTotalPrice {
                                            amount
                                            currencyCode
                                        }
                                        originalTotalPrice {
                                            amount
                                            currencyCode
                                        }

                                        variant {
                                            id
                                            priceV2 {
                                                amount
                                                currencyCode
                                            }
                                            sku
                                            selectedOptions {
                                                name
                                                value
                                            }
                                            image {
                                                altText
                                                url
                                                id
                                            }
                                            product {
                                                handle
                                                id
                                                vendor
                                            }

                                        }
                                    }
                                }
                            }
                            name
                            orderNumber
                            statusUrl
                            shippingAddress {
                                name
                                address1
                                address2
                                city
                                company
                                province
                                zip
                                country
                                phone
                            }
                            totalShippingPriceV2 {
                                amount
                                currencyCode
                            }
                            totalRefundedV2 {
                                amount
                                currencyCode
                            }
                            successfulFulfillments {
                                trackingCompany
                                trackingInfo {
                                    number
                                    url
                                }

                            }
                            totalTaxV2 {
                                amount
                                currencyCode
                            }

                        }
                    }
                    pageInfo {
                        hasNextPage
                        hasPreviousPage
                        endCursor
                        startCursor
                    }
                }
            }
        }
        `,
		variables: { customerAccessToken },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function resetCustomerPassword(email) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerRecover($email: String!) {
            customerRecover(email: $email) {
            customerUserErrors {
                code
                field
                message
            }
            }
        }
        `,
		variables: { email },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function updateCustomerPassword(resetUrl, password) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation resetPasswordByUrl($resetUrl: URL!, $password: String!) {
            customerResetByUrl(resetUrl: $resetUrl, password: $password) {
              customer { id }
              customerUserErrors { code field message}
            }
          }
        `,
		variables: { resetUrl, password },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function removeCustomerAddress(customerAccessToken, id) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerAddressDelete($customerAccessToken: String!, $id: ID!) {
            customerAddressDelete(customerAccessToken: $customerAccessToken, id: $id) {
            customerUserErrors {
                code
                field
                message
            }
            deletedCustomerAddressId
            }
        }
        `,
		variables: { customerAccessToken, id },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function updateCustomerAddress(address, customerAccessToken, id) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerAddressUpdate($address: MailingAddressInput! , $customerAccessToken: String!, $id: ID!, ) {
            customerAddressUpdate(address: $address, customerAccessToken: $customerAccessToken, id: $id, ) {
            customerUserErrors {
                code
                field
                message
            }
            customerAddress {
                id
                firstName
                lastName
                address1
                address2
                city
                province
                provinceCode
                zip
                company
                country
                countryCodeV2
                formatted
                formattedArea
                phone

            }
            }
        }
        `,
		variables: { address, customerAccessToken, id },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function customerAddressCreate(address, customerAccessToken) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerAddressCreate($address: MailingAddressInput! , $customerAccessToken: String!) {
            customerAddressCreate(address: $address, customerAccessToken: $customerAccessToken ) {
            customerUserErrors {
                code
                field
                message
            }
            customerAddress {
                id
                firstName
                lastName
                address1
                address2
                city
                province
                provinceCode
                zip
                company
                country
                countryCodeV2
                formatted
                formattedArea
                phone

            }
            }
        }
        `,
		variables: { address, customerAccessToken },
	});

	//

	return {
		statusCode: 200,
		data: data,
	};
}

export async function customerDefaultAddressUpdate(
	addressId,
	customerAccessToken
) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerDefaultAddressUpdate($addressId: ID!, $customerAccessToken: String!) {
            customerDefaultAddressUpdate(addressId: $addressId, customerAccessToken: $customerAccessToken) {
                customerUserErrors {
                    code
                    field
                    message
                  }
                  customer {
                    addresses (first: 50) {
                      edges {
                        node {
                          id
                          firstName
                          lastName
                          address1
                          address2
                          city
                          company
                          country
                          countryCodeV2
                          name
                          province
                          provinceCode
                          zip
                          formatted
                          formattedArea
                          phone
                        }
                      }
                    }
                    defaultAddress {
                      id
                      firstName
                      lastName
                      address1
                      address2
                      city
                      company
                      country
                      countryCodeV2
                      name
                      province
                      provinceCode
                      zip
                    }
                  }
            }
        }
        `,
		variables: { addressId, customerAccessToken },
	});

	//

	return {
		statusCode: 200,
		data: data,
	};
}

export async function updateCustomer(customer, customerAccessToken) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        mutation customerUpdate($customer: CustomerUpdateInput!, $customerAccessToken: String!) {
            customerUpdate(customer: $customer, customerAccessToken: $customerAccessToken) {
            customer {
                id
                firstName
                lastName
                email
                phone
            }
            customerUserErrors {
                code
                field
                message
            }
            }
        }
        `,
		variables: { customer, customerAccessToken },
	});

	return {
		statusCode: 200,
		data: data,
	};
}

export async function getProductByHandle(handle) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query productByHandle($handle: String!) {
            productByHandle (handle: $handle) {
                    id
                    title
                    handle
                    descriptionHtml
                    seo {
                        title
                        description
                    }
                }
            }
        `,
		variables: { handle },
	});

	return {
		statusCode: 200,
		data: data?.productByHandle
	};
}

export const registryLogIn = async (email, password) => {
	let reqData = {
		query: `
                mutation login_user($email: String!, $password: String!){
                loginUser(loginInput:{
                    email:$email,
                    password:$password
                },
                ){
                    user{
                    id
                    },
                    registries
                    token,
                    redirectionUrl
                }
                }
        `,
		operationName: 'login_user',
		variables: {
			email: email,
			password: password,
		},
	};

	let response = await axios.post(
		'https://registry-api.lilhathee.com/api/graphql', // https://registry-api.monicaandandy.com/api/graphql
		reqData,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	// if (
	//     response.status == 200 &&
	//     response.data.data?.loginUser?.token &&
	//     response.data.data?.loginUser?.registries.length > 0
	// ) {
	//     localStorage.setItem('hasRegistry', 'true')
	//     registryData.set({...registryData.get(), 'hasRegistry': true})
	//     localStorage.setItem(
	//         'registry_token',
	//         response.data.data.loginUser.token
	//     )
	//     localStorage.setItem(
	//         'registryId',
	//         JSON.stringify(response.data.data.loginUser.registries[0].id)
	//     )
	// }
	return response.data?.data?.loginUser;
};

export const getRegistry = async (email) => {
	let reqData = {
		query: `
        query findRegistry($registryName: String!){
            findRegistry(registryName: $registryName){
            edges{
                node{
                id,
                name,
                displayName,
                urlSlug,
                createdTs,
                dueDate,
                user{
                    id,
                    defaultAddress{
                    city,
                    province
                    }
                }
                }
            }
            }
        }
        `,
		variables: {
			registryName: email,
		},
	};

	let response = await axios.post(
		'https://registry-api.monicaandandy.com/api/graphql',
		reqData,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);
};

export const REGISTER_USER = `
mutation register_user($name: String!, $email: String!, $password: String!){
  registerUser(registerInput:{
      name:$name,
      email:$email,
      password:$password
  }
 ){
    user{
      id
    },
    token,
    redirectionUrl
  }
}
`;

export const CREATE_REGISTRY = `
    mutation{
      createRegistry{
        registry{
          id,
          user {
            email,
            firstName,
            lastName,
          }
        },
        message,
        status
      }
    }
`;

export const SAVE_QUESTION_RESPONSE = `
mutation addRegistryQuestionOption($registryId: Int!, $answers: [RegistryQuestionOptionInput]!, $isFromOnboarding: Boolean){
  addRegistryMultipleQuestionOption(registryId: $registryId, registryQuestionOptionInput: $answers, isFromOnboarding: $isFromOnboarding){
    message
  }
}
`;

export const LOGIN_USER = `
mutation login_user($email: String!, $password: String!, $returnTo: String){
  loginUser(loginInput:{
      email:$email,
      password:$password
  },
  returnTo: $returnTo
 ){
    user{
      id
    },
    registries
    token,
    redirectionUrl
  }
}
`;

export const GET_REGISTRIES = `
query {
  registries {
    edges {
      node {
        id
        name
        urlSlug
        greeting
        displayName
        isFeaturedNoticeRead
        isFeatured
        isMustHaveApplied
        isMaButtonConfigured
        isBuildRegistryVisited
        isViewAsGuestVisited
        isFeaturedOrderApplied
        isGroupGiftApplied
        isViewRegistryVisited
        isCashInLieuItemAdded
        isCashFundItemAdded
        isGroupGiftItemAdded
        isOnboardingCompleted
        quizMode
        image {
          id
          image
        }
        dueDate
        isPublic
        createdTs
        user{
          isCurator
          bankAccounts{
            id
          }
          defaultAddress{
            id
            addressId
            firstName
            lastName
            address1
            address2
            city
            company
            province
            country
            zip
            phone
            provinceCode
            countryCode

          }
        }
      }
    }
  }
}
`;

export const UPDATE_REGISTRY = `
  mutation updateRegistryDetail($registryId: Int!, $input: RegistryInput!){
    updateRegistry(registryId: $registryId, registryData: $input) {
      registry{
      name,
      image{
            id,image
        },
      isCashInLieuItemAdded,
      isCashFundItemAdded,
      isGroupGiftItemAdded
      }
    }
  }
`;

export const FIND_REGISTRY = `
  query findRegistry($registryName: String!){
    findRegistry(registryName: $registryName){
      edges{
        node{
          id,
          name,
          displayName,
          urlSlug,
          createdTs,
          dueDate,
          user{
            id,
            defaultAddress{
              city,
              province
            }
          }
        }
      }
    }
  }
`;

export async function getLatestBlogArticles() {
	const data = await sendShopifyStorefrontRequest({
		query: `
    query {
        blog(handle: "ma-edit") {
            id
            articles(
              first: 250
              after: "eyJsYXN0X2lkIjozODcwNTM1ODQ0NDYsImxhc3RfdmFsdWUiOiIyMDE5LTAzLTA3IDA2OjAwOjAwLjAwMDAwMCJ9"
              sortKey: PUBLISHED_AT
            ) {
              edges {
                node {
                  id
                  title
                  handle
                  excerpt
                  excerptHtml
                  tags
                  publishedAt
                  image {
                    url
                    altText
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
      }
        `,
		variables: {},
	});
	let articles = data.blog.articles.edges.reverse();
	return articles.slice(0, 12);
}

export async function getArticlesByTag(handle, tag) {
	const data = await sendShopifyStorefrontRequest({
		query: `
        query getBlogArticlesByTag($handle: String!, $tag: String!) {
            blog(handle: $handle) {
                id
                articles(
                  first: 10,
                  query: $tag
                ) {
                  edges {
                    node {
                      id
                      title
                      handle
                      excerpt
                      excerptHtml
                      tags
                      publishedAt
                      image {
                        url
                        altText
                      }
                    }
                  }
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                }
              }
          }

        `,
		variables: { handle: 'ma-edit', tag },
	});
	let articles = data.blog.articles.edges;
	return articles;
}

export async function getProductInventory(productID) {
	const productGID = 'gid://shopify/Product/' + productID;

	let data = await sendShopifyStorefrontRequest({
		query: `
    query getProductById($id: ID!) {
      product(id: $id){
        title
        id
        variants(first:100) {
          nodes {
              id
              quantityAvailable
          }
        }
      }
  }`,
		variables: { id: productGID },
	});

	if (data.product != null) {
		data.product.id = data.product.id.split('Product/')[1];
		data.product.variants.nodes = data.product.variants.nodes.map((v) => {
			v.id = v.id.split('ProductVariant/')[1];
			return v;
		});

		return data;
	} else {
		return { id: productID, inventory: 0 };
	}
}
